
<template>
  <form
    class="form-horizontal"
    aria-labelledby="customer-admin-usersheader"
    @submit.prevent="search()"
  >
    <h2
      id="customer-admin-usersheader"
      class="sr-only"
    >
      Admin User Search Form
    </h2>
    <admin-stats
      :users="users"
    />
    <div
      class="form-group"
      style="padding: 20px;"
    >
      <label
        for="nameEmailSearch"
        class="control-label"
      >
        Search Users by Email or Name:
      </label>
      <input
        id="nameEmailSearch"
        v-model.trim="match"
        type="text"
        class="form-control"
        placeholder="enter full or partial email or name"
        name="match"
        style="width: 300px;"
        required
        @keyup="search"
      >
    </div>
    <div v-if="allUsers && allUsers.length >= 1000">
      There are too many total users to display a full list. Use the search box above
      to narrow the results.
    </div>
    <table
      v-if="allUsers && allUsers.length < 1000"
      class="table table-striped table-bordered table-condensed"
    >
      <caption>List of all users</caption>
      <thead>
        <tr>
          <th
            width="20%"
            scope="col"
          >
            Name
          </th>
          <th
            width="20%"
            scope="col"
          >
            Email
          </th>
          <th
            width="20%"
            scope="col"
          >
            Status
          </th>
          <th
            width="20%"
            scope="col"
          >
            Active Groups
          </th>
          <th
            width="20%"
            scope="col"
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="user in allUsers"
        >
          <td>{{ user.name }}</td>
          <td>{{ user.email }}</td>
          <td>
            <span :class="{ 'text-success': user.membershipStatus === 'active', 'text-danger': user.membershipStatus !== 'active' }">
              <svg
                v-if="user.membershipStatus === 'active'"
                class="icon-check"
              >
                <use xlink:href="/static/svg-symbols.svg#icon-check" />
              </svg>
              <svg
                v-else
                class="icon-plus"
              >
                <use xlink:href="/static/svg-symbols.svg#icon-plus" />
              </svg>
              <strong>{{ user.membershipStatus | startCase }}</strong>
              <span v-if="user.membershipType">
                ({{ user.membershipType }})
              </span>
            </span>
          </td>
          <td>
            <div
              v-for="group in user.groups"
              v-if="group.groupName && group.membershipStatus === 'active'"
            >
              <strong>{{ group.groupName }}</strong>: {{ (group.roles) ? group.roles.join(', ') : '' }}
            </div>
          </td>
          <td>
            <router-link
              :to="{ name: 'customer-admin-users-userId', params: { userId: user.userId } }"
            >
              View User
            </router-link>
          </td>
        </tr>
        <tr v-if="textEntered && !resultsFound">
          <td colspan="5">
            <h2 class="text-center">
              No results
            </h2>
          </td>
        </tr>
      </tbody>
    </table>
  </form>
</template>

<style lang="less" scoped>
.icon-check {
  fill: #04D97E !important;
  position: relative;
  top: 2px;
  height: 16px; width: 16px;
}
.icon-plus {
  transform: rotate(45deg);
  fill: #D3382F !important;
  position: relative;
  top: 2px;
  height: 16px; width: 16px;
}
</style>

<script>
import AdminStats from '../forms/AdminStats';

export default {
  components: {AdminStats},
  props: {
    org: {
      type: Object,
      required: true
    },
    users: {
      type: Object,
      required: true
    },
    isEdu: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      required: true
    },
    groupIdsToNames: Object,
  },
  data() {
    return {
      match: null,
      matched: [],
    };
  },
  computed: {
    textEntered: {
      cache: false,
      get() {
        return !!(this.match && this.match.length);
      }
    },
    resultsFound: {
      cache: false,
      get() {
        return !!(this.matched && this.matched.length);
      }
    },
    allUsers: {
      cache: false,
      get() {
        return (!this.textEntered) ? this.flatUsers : this.matched;
      }
    },
    flatUsers() {
      let users = {};
      this._.forEach(this.users, (group, groupId) => {
        this._.forEach(group, (u, userId) => {
          if (!users[userId]) {
            users[userId] = this._.cloneDeep(u);
            users[userId].groups = [];
            users[userId].membershipStatus = 'inactive';
            users[userId].membershipType = '';
          }

          if (u.membershipStatus === 'active' && !u.roles) {
            users[userId].membershipType = 'coach/admin'; // org-level
          } else if (u.membershipStatus === 'active' && u.roles) {
            users[userId].membershipStatus = 'active';
            if (u.roles.indexOf('manager') !== -1 || u.roles.indexOf('admin') !== -1) {
              users[userId].membershipType = 'coach/admin';
            } else if (u.roles.indexOf('speaker') !== -1 && users[userId].membershipType != 'coach/admin') {
              users[userId].membershipType = 'speaker';
            }
          }

          users[userId].membershipStatus = (users[userId].membershipStatus === 'active') ?
            'active' : u.membershipStatus;
          users[userId].groups.push({
            groupId: groupId,
            roles: u.roles,
            membershipStatus: u.membershipStatus,
            groupName: this.groupIdsToNames[groupId],
          });
        });
      });

      users = this._.sortBy(
        users,
        [(u) => u.lastName.toLowerCase(), (u) => u.firstName.toLowerCase()]
      );

      return users;
    }
  },
  methods: {
    search(evt) {
      if (!this.textEntered) {
        this.matched = [];
      }

      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      this.matched = [];

      this._.forEach(this.flatUsers, (user) => {
        if (!user.name || !user.email) {
          return;
        }
        if (user.email.toLowerCase().indexOf(
          this.match.toLowerCase()) !== -1 ||
          user.name.toLowerCase().indexOf(this.match.toLowerCase()) !== -1) {
          this.matched.push(user);
        }
      });

      this.matched = _.sortBy(_.uniqBy(this.matched, 'userId'),
        [(u) => u.lastName.toLowerCase(), (u) => u.firstName.toLowerCase()]
      );
    }
  }
};
</script>
